import type { StringKey } from '../../../../lang/index';
import type { FieldOption } from '../constants/field-options-registry.constant';

import { VariableMappingType } from '../enums/mapping-editor.const';
import {
  grantAgreementBaseFieldLabelKey,
  grantPlanTypeFieldLabelKey,
  vestingTargetBasedFieldLabelKey,
  vestingTimeBasedDynamicFieldLabelKey,
  vestingTimeBasedStandardFieldLabelKey,
} from '../enums/template-field.const';

const fieldValueToLabelKey: Record<string, StringKey> = {
  ...grantAgreementBaseFieldLabelKey,
  ...grantPlanTypeFieldLabelKey,
  ...vestingTimeBasedStandardFieldLabelKey,
  ...vestingTimeBasedDynamicFieldLabelKey,
  ...vestingTargetBasedFieldLabelKey,
};

const milestoneIndexed = /^milestone-(\d+)-(name|weight|target-date)$/;
const periodIndexed = /^period-(\d+)-(duration|frequency|allocation)$/;

const milestoneSuffixToLabelKey: Record<string, StringKey> = {
  name: 'MILESTONE_N_NAME',
  weight: 'MILESTONE_N_WEIGHT',
  'target-date': 'MILESTONE_N_TARGET_DATE',
};

const periodSuffixToLabelKey: Record<string, StringKey> = {
  duration: 'PERIOD_N_DURATION',
  frequency: 'PERIOD_N_FREQUENCY',
  allocation: 'PERIOD_N_ALLOCATION',
};

const parseIndexedFieldLabel = (
  storedValue: string,
): { labelKey: StringKey; params: Record<string, number> } | null => {
  const milestoneMatch = milestoneIndexed.exec(storedValue);
  if (milestoneMatch) {
    const n = Number(milestoneMatch[1]);
    const suffix = milestoneMatch[2];
    const labelKey = milestoneSuffixToLabelKey[suffix];
    if (labelKey) return { labelKey, params: { n } };
  }

  const periodMatch = periodIndexed.exec(storedValue);
  if (periodMatch) {
    const n = Number(periodMatch[1]);
    const suffix = periodMatch[2];
    const labelKey = periodSuffixToLabelKey[suffix];
    if (labelKey) return { labelKey, params: { n } };
  }

  return null;
};

export const getFieldMappingDisplayLabel = (params: {
  fieldOptions?: FieldOption[];
  mappingTypeExplicit?: VariableMappingType;
  messagesLocale: Record<string, string>;
  messagesLocaleFn: (key: StringKey, values?: Record<string, number | string>) => string;
  storedValue: string;
  variableMappingTypeFromStore?: VariableMappingType;
}): string => {
  const isCustom =
    params.mappingTypeExplicit === VariableMappingType.CUSTOM ||
    params.variableMappingTypeFromStore === VariableMappingType.CUSTOM;
  if (isCustom) {
    return params.storedValue;
  }

  if (params.fieldOptions?.length) {
    const option = params.fieldOptions.find((o) => o.value === params.storedValue);
    if (option) {
      return option.labelKeyParams
        ? params.messagesLocaleFn(option.labelKey, option.labelKeyParams)
        : params.messagesLocale[option.labelKey];
    }
  }

  const labelKey = fieldValueToLabelKey[params.storedValue];
  if (labelKey) {
    return params.messagesLocale[labelKey];
  }

  const indexed = parseIndexedFieldLabel(params.storedValue);
  if (indexed) {
    return params.messagesLocaleFn(indexed.labelKey, indexed.params);
  }

  return params.storedValue;
};
